import { ChevronDownIcon } from '@chakra-ui/icons'
import { Box, Button, Flex, useBreakpointValue } from '@chakra-ui/react'
import ImageBox from 'components/common/ImageBox'
import { LandingContainer } from 'components/common/LandingContainer'
import ArrowRightOmens from 'components/svg/ArrowRightOmens'
import { Body1, Body1Strong, H2, H3, H5 } from 'components/typography'
import { MEDLEVE_HOW_IT_WORKS_STEPS } from 'constants/medleveHowItWorks'
import { ONBOARDING_INITIAL_STEP } from 'constants/onboarding'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { FC } from 'react'

import MedleveScrollingBannerSection from './MedleveScrollingBannerSection'

const MedleveHowItWorksSection: FC = () => {
  const isMobile = useBreakpointValue({ base: true, sm: false })
  const { pathname } = useRouter()
  const gads = pathname.includes('gads')
  return (
    <Box bg="alabaster">
      <LandingContainer
        bg="alabaster"
        py={{ base: 6, lg: 20 }}
        gap={{ base: 10, lg: 14 }}
        align={'center'}
        maxW={'1400px'}
      >
        <MedleveScrollingBannerSection />
        <Flex
          w={'full'}
          direction={{ base: 'column', lg: 'row' }}
          justify={'space-between'}
          px={{ lg: '60px' }}
          gap={{ base: 6, lg: 36 }}
        >
          <Flex gap={4} direction="column" flex={1}>
            <H2 mb={4}>
              Como <br /> funciona
            </H2>
            {isMobile && (
              <Body1 maxW={'500px'}>
                Esse tratamento oferece acompanhamento médico especializado e prescrição personalizada de medicamentos
                para emagrecimento - como os análogos de GLP-1, caso sejam indicados.
              </Body1>
            )}
            <Flex bg={'white'} borderRadius={{ base: '24px', lg: '32px' }} direction={'column'} p={6} gap={2}>
              <Flex justify={'space-between'} align={'center'}>
                <H3
                  fontWeight={600}
                  fontSize={{ base: '14px', lg: '15px' }}
                  lineHeight={{ base: '18px', lg: '20px' }}
                  letterSpacing="-0.01em"
                >
                  1. {gads ? 'Responda a um breve questionário' : MEDLEVE_HOW_IT_WORKS_STEPS[0].title}
                </H3>{' '}
                <ChevronDownIcon fontSize={'24px'} color={'ishafit.70'} />
              </Flex>
              <Body1>
                {gads
                  ? 'Na pré-consulta, você seleciona o profissional desejado para realizar uma consulta.'
                  : MEDLEVE_HOW_IT_WORKS_STEPS[0].description}
              </Body1>
              {!gads && <Body1 fontStyle="italic">{MEDLEVE_HOW_IT_WORKS_STEPS[0].footnote}</Body1>}
            </Flex>
            <Flex bg={'white'} borderRadius={{ base: '24px', lg: '32px' }} direction={'column'} p={6} gap={2}>
              <Flex justify={'space-between'} align={'center'}>
                <H3
                  fontWeight={600}
                  fontSize={{ base: '14px', lg: '15px' }}
                  lineHeight={{ base: '18px', lg: '20px' }}
                  letterSpacing="-0.01em"
                >
                  2. {gads ? 'Faça uma consulta com um(a) especialista' : MEDLEVE_HOW_IT_WORKS_STEPS[1].title}
                </H3>{' '}
                <ChevronDownIcon fontSize={'24px'} color={'ishafit.70'} />
              </Flex>

              <Body1>
                {gads
                  ? 'Após escolher a forma de contato e realizar o pagamento, você faz sua consulta com um(a) especialista.'
                  : MEDLEVE_HOW_IT_WORKS_STEPS[1].description}
              </Body1>
            </Flex>
            <Flex bg={'white'} borderRadius={{ base: '24px', lg: '32px' }} direction={'column'} p={6} gap={2}>
              <Flex justify={'space-between'} align={'center'}>
                <H3
                  fontWeight={600}
                  fontSize={{ base: '14px', lg: '15px' }}
                  lineHeight={{ base: '18px', lg: '20px' }}
                  letterSpacing="-0.01em"
                >
                  3. {gads ? 'Acompanhe e avance seu tratamento' : MEDLEVE_HOW_IT_WORKS_STEPS[2].title}
                </H3>{' '}
                <ChevronDownIcon fontSize={'24px'} color={'ishafit.70'} />
              </Flex>
              <Body1>
                {gads
                  ? 'Vá na farmácia da sua escolha, faça um acompanhamento do seu tratamento e peça ajustes quando necessário.'
                  : MEDLEVE_HOW_IT_WORKS_STEPS[2].description}
              </Body1>
            </Flex>
          </Flex>
          <Flex flex={1} direction={'column'} gap={10}>
            {!isMobile && !gads && (
              <Body1 maxW={'500px'}>
                Esse tratamento oferece acompanhamento médico especializado e prescrição personalizada de medicamentos
                para emagrecimento - como os análogos de GLP-1, caso sejam indicados.
              </Body1>
            )}

            <Flex flex={1} borderRadius={{ base: '24px', lg: '32px' }} overflow={'hidden'}>
              <ImageBox
                src="/landings/caneta-emagrecedora-azul.webp"
                alt="X"
                w={'full'}
                h={'full'}
                alignItems={'center'}
              >
                <Flex align={'center'} justify={'flex-end'} direction={'column'} w={'full'} h={'full'} py={12}>
                  <Link href={`/onboarding/weight_loss/${ONBOARDING_INITIAL_STEP}`}>
                    <Button
                      h={'full'}
                      maxH={'max-content'}
                      w={{ base: '224px', lg: '304px' }}
                      px={{ base: '28px', lg: '32px' }}
                      py={{ base: '28px', lg: '40px' }}
                      borderRadius={{ base: '24px', lg: '32px' }}
                      color={'alabaster'}
                      bg="rgba(246, 244, 232, 0.0)"
                      backdropFilter="blur(24px)"
                      _hover={{ bg: 'rgba(246, 244, 232, 0.0)' }}
                    >
                      <Flex w={'full'} justify={'space-between'} align={'center'}>
                        {isMobile ? (
                          <>
                            <Body1
                              fontWeight={800}
                              fontSize={{ base: '1rem', lg: '1.5rem' }}
                              letterSpacing={{ base: '-0.01em', lg: '-0.03em' }}
                              lineHeight={{ base: '1.25rem', lg: '1.75rem' }}
                            >
                              Começar
                            </Body1>
                            <ArrowRightOmens fill="alabaster" boxSize={{ base: '14px', lg: '18px' }} />
                          </>
                        ) : (
                          <Body1
                            fontWeight={800}
                            fontSize={{ base: '1rem', lg: '1.5rem' }}
                            letterSpacing={{ base: '-0.01em', lg: '-0.03em' }}
                            lineHeight={{ base: '1.25rem', lg: '1.75rem' }}
                          >
                            Começar agora
                          </Body1>
                        )}
                      </Flex>
                    </Button>
                  </Link>
                </Flex>
              </ImageBox>
            </Flex>
          </Flex>
        </Flex>
      </LandingContainer>
    </Box>
  )
}

export default MedleveHowItWorksSection
